import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { ChipModule } from "primeng/chip";
import { DropdownModule } from "primeng/dropdown";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { FloatLabelModule } from "primeng/floatlabel";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { TooltipModule } from "primeng/tooltip";
import { Subject, takeUntil } from "rxjs";
import {
  FileContentListComponent,
  FileInfo,
} from "src/app/common/components/file-content-list/file-content-list.component";
import { AccountInfo } from "src/app/common/models/account.model";
import { AccountService } from "src/app/common/services/account.service";
import { ConfigService } from "src/app/common/services/config.service";
import { LlmConfigService } from "src/app/common/services/llm-config.service";
import { DocumentsSpace } from "src/app/modules/documents-space/documents-space.model";
import { DocumentsSpaceService } from "src/app/modules/documents-space/documents-space.service";

@Component({
  selector: "app-documents-space-settings",
  standalone: true,
  imports: [
    ButtonModule,
    TooltipModule,
    FloatLabelModule,
    InputTextModule,
    FormsModule,
    MultiSelectModule,
    ChipModule,
    DropdownModule,
  ],
  templateUrl: "./documents-space-settings.component.html",
  styleUrl: "./documents-space-settings.component.scss",
})
export class DocumentsSpaceSettingsComponent implements OnInit, OnDestroy {
  protected readonly accountService = inject(AccountService);

  private readonly llmConfigService = inject(LlmConfigService);
  private readonly documentsSpaceService = inject(DocumentsSpaceService);
  protected readonly ref = inject(DynamicDialogRef);
  protected readonly dialogConfig: DynamicDialogConfig<{
    documentsSpace: DocumentsSpace;
  }> = inject(DynamicDialogConfig);
  private readonly dialogService = inject(DialogService);
  private readonly configService = inject(ConfigService);
  private readonly destroySubject = new Subject<void>();

  uploadedFilesListRef: DynamicDialogRef | undefined;
  protected settingsMode = "CREATE";
  documentsSpaceName = "";
  documentsSpaceOwnerName = "";
  selectedUsers: string[] = [];
  usersOptions: AccountInfo[] = [];
  selectedAdmins: string[] = [];
  files: FileInfo[] = [];
  modelOptions = Object.values(this.llmConfigService.llmConfigs).filter((llm) => llm.gdprCompliant);
  selectedModel = this.configService.defaultLlm;
  documentsSpaceId = "";

  async ngOnInit(): Promise<void> {
    this.usersOptions = await this.accountService.getAllAccounts();
    if (this.dialogConfig.data?.documentsSpace) {
      this.documentsSpaceName = this.dialogConfig.data.documentsSpace.name;
      this.documentsSpaceOwnerName = this.dialogConfig.data.documentsSpace.ownerName;
      this.selectedUsers = this.dialogConfig.data.documentsSpace.sharedWithUsers;
      this.selectedAdmins = this.dialogConfig.data.documentsSpace.admins;
      this.documentsSpaceId = this.dialogConfig.data.documentsSpace.id;
      this.files = this.dialogConfig.data.documentsSpace.files;
      this.selectedModel = this.dialogConfig.data.documentsSpace.model;
      this.settingsMode = "EDIT";
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  async createOrEdit(): Promise<void> {
    if (this.settingsMode === "CREATE") {
      await this.documentsSpaceService.create(
        this.documentsSpaceName,
        this.selectedModel,
        this.selectedUsers,
        this.selectedAdmins,
        this.files.map((file) => file.id),
      );
      this.ref.close(true);
      return;
    }
    const res = await this.documentsSpaceService.edit(
      this.documentsSpaceId,
      this.documentsSpaceName,
      this.selectedModel,
      this.selectedUsers,
      this.selectedAdmins,
      this.files.map((file) => file.id),
    );
    this.ref.close(res);
  }

  onFileSelected(files: FileInfo[]): void {
    const selectedFileIds = new Set(files.map((file) => file.id));
    this.files = this.files.filter((file) => !selectedFileIds.has(file.id));
    this.files = this.files.concat(files);
  }

  removeSelectedFile(id: string): void {
    this.files = this.files.filter((file) => file.id !== id);
  }

  openDialogToAddFiles(): void {
    this.uploadedFilesListRef = this.dialogService.open(FileContentListComponent, {
      header: $localize`:@@documents_space_settings_add_files:Ajouter des fichiers à l'espace`,
      width: "70%",
      data: {
        selectionMode: true,
        onlyWithFilePath: true,
      },
    });
    this.uploadedFilesListRef.onClose.pipe(takeUntil(this.destroySubject)).subscribe((selectedFiles: FileInfo[]) => {
      if (selectedFiles) {
        this.onFileSelected(selectedFiles);
      }
    });
  }
}
